import React, { useState, useRef, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './photoFocus.css';

const PhotoFocus = ({ image, onClose, onSave }) => {
  const [focusPoint, setFocusPoint] = useState({ x: 50, y: 50 }); // Standardværdi centreret
  const [originalPoint, setOriginalPoint] = useState({ x: 50, y: 50 });
  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Hent eksisterende fokuspunkt for billedet ved indlæsning
  useEffect(() => {
    const fetchImageFocus = async () => {
      try {
        if (image && image.name) {
          const heroDocRef = doc(db, 'content', 'hero');
          const heroDoc = await getDoc(heroDocRef);
          
          if (heroDoc.exists()) {
            const images = heroDoc.data().images || [];
            const currentImage = images.find(img => img.url === image.url);
            
            if (currentImage && currentImage.focusPoint) {
              setFocusPoint(currentImage.focusPoint);
              setOriginalPoint(currentImage.focusPoint);
            }
          }
        }
      } catch (error) {
        console.error('Fejl ved hentning af billedefokus:', error);
      }
    };
    
    fetchImageFocus();
  }, [image]);

  const handleImageClick = (e) => {
    if (!imageRef.current || !imageContainerRef.current) return;
    
    // Beregn klikkposition i forhold til billedet
    const rect = imageContainerRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    // Konverter til procent
    const percentX = (offsetX / rect.width) * 100;
    const percentY = (offsetY / rect.height) * 100;
    
    // Opdater fokuspunkt
    setFocusPoint({
      x: Math.round(percentX * 100) / 100, // Afrund til 2 decimaler
      y: Math.round(percentY * 100) / 100
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Valider at input er et tal mellem 0 og 100
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    
    const clampedValue = Math.max(0, Math.min(100, numValue));
    
    setFocusPoint(prev => ({
      ...prev,
      [name]: clampedValue
    }));
  };

  const handleSave = async () => {
    try {
      // Opdater fokuspunkt i Firestore
      const heroDocRef = doc(db, 'content', 'hero');
      const heroDoc = await getDoc(heroDocRef);
      
      if (heroDoc.exists()) {
        const images = heroDoc.data().images || [];
        const updatedImages = images.map(img => {
          if (img.url === image.url) {
            return {
              ...img,
              focusPoint: focusPoint
            };
          }
          return img;
        });
        
        await setDoc(heroDocRef, {
          ...heroDoc.data(),
          images: updatedImages
        });
        
        // Kald callback med opdateret data
        onSave && onSave(image.url, focusPoint);
      }
    } catch (error) {
      console.error('Fejl ved gem af billedefokus:', error);
      alert('Der opstod en fejl ved gem af fokuspunkt.');
    }
  };

  const handleReset = () => {
    setFocusPoint(originalPoint);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div className="photo-focus-container">
      <div className="photo-focus-header">
        <h2 className="photo-focus-title">Vælg billedefokus</h2>
        <button 
          className="photo-focus-close" 
          onClick={onClose}
          aria-label="Luk"
        >
          ×
        </button>
      </div>
      
      <div 
        className="photo-focus-image-container" 
        ref={imageContainerRef}
        onClick={handleImageClick}
      >
        <img 
          src={image.url}
          alt="Vælg fokuspunkt"
          className="photo-focus-image"
          ref={imageRef}
          onLoad={handleImageLoad}
        />
        {imageLoaded && (
          <>
            <div 
              className="photo-focus-point" 
              style={{ 
                left: `${focusPoint.x}%`, 
                top: `${focusPoint.y}%` 
              }}
            />
            <div 
              className="photo-focus-ripple" 
              style={{ 
                left: `${focusPoint.x}%`, 
                top: `${focusPoint.y}%` 
              }}
            />
          </>
        )}
      </div>
      
      <div className="photo-focus-coords">
        <div className="photo-focus-input-group">
          <label className="photo-focus-label" htmlFor="x">X-position (venstre til højre)</label>
          <input
            type="number"
            name="x"
            id="x"
            className="photo-focus-input"
            value={focusPoint.x}
            onChange={handleInputChange}
            min="0"
            max="100"
            step="0.1"
          />
          <p className="photo-focus-help-text">0 = venstre, 100 = højre</p>
        </div>
        
        <div className="photo-focus-input-group">
          <label className="photo-focus-label" htmlFor="y">Y-position (top til bund)</label>
          <input
            type="number"
            name="y"
            id="y"
            className="photo-focus-input"
            value={focusPoint.y}
            onChange={handleInputChange}
            min="0"
            max="100"
            step="0.1"
          />
          <p className="photo-focus-help-text">0 = top, 100 = bund</p>
        </div>
      </div>
      
      <div className="photo-focus-preview">
        <h3 className="photo-focus-preview-title">Forhåndsvisning</h3>
        <div className="photo-focus-preview-container">
          <img 
            src={image.url}
            alt="Forhåndsvisning af fokuspunkt"
            className="photo-focus-preview-image"
            style={{
              '--focus-x': `${focusPoint.x}%`,
              '--focus-y': `${focusPoint.y}%`
            }}
          />
        </div>
      </div>
      
      <div className="photo-focus-buttons">
        <button 
          className="photo-focus-button photo-focus-reset"
          onClick={handleReset}
        >
          Nulstil
        </button>
        <button 
          className="photo-focus-button photo-focus-save"
          onClick={handleSave}
        >
          Gem fokuspunkt
        </button>
        <button 
          className="photo-focus-button photo-focus-cancel"
          onClick={onClose}
        >
          Annuller
        </button>
      </div>
    </div>
  );
};

export default PhotoFocus;
